import { useState, useEffect } from 'react';
import { Input, Text, Colors } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import { useDashboardGroupName } from 'state/dashboard';
import { STATE_CODE_TO_LABEL } from 'utils/constants/location';

const labelStyle = {
  fontSize: '12px',
  color: Colors.ArmyGreen900,
};

const inputStyle = {
  maxWidth: '400px',
};

type SearchNameStepProps = {
  searchName: string;
  onSearchNameChange: (name: string) => void;
  selectedState: string | null;
  selectedVerticals: number[];
  allVerticals: Array<{ id: number; name: string; sector: { id: number; name: string } }>;
};

const SearchNameStep = ({
  searchName,
  onSearchNameChange,
  selectedState,
  selectedVerticals,
  allVerticals,
}: SearchNameStepProps) => {
  const [, setGroupName] = useDashboardGroupName();
  const [localName, setLocalName] = useState(searchName);

  // Generate auto-filled name from state and first sector (only on initial mount or when searchName is empty)
  useEffect(() => {
    // Only auto-fill if searchName is empty (not set by user yet)
    if (
      !searchName &&
      selectedState &&
      selectedVerticals.length > 0 &&
      allVerticals.length > 0
    ) {
      const stateLabel =
        STATE_CODE_TO_LABEL[selectedState as keyof typeof STATE_CODE_TO_LABEL] ||
        selectedState;

      // Get the first selected vertical and its sector
      const firstVertical = allVerticals.find((v) => selectedVerticals.includes(v.id));
      const sectorName = firstVertical?.sector?.name || '';

      if (sectorName) {
        const autoFilledName = `${stateLabel} ${sectorName}`;
        setLocalName(autoFilledName);
        onSearchNameChange(autoFilledName);
        setGroupName(autoFilledName);
      }
    }
  }, [selectedState, selectedVerticals, allVerticals]); // Only depend on these, not searchName

  // Sync localName with searchName prop when it changes externally
  useEffect(() => {
    if (searchName !== localName) {
      setLocalName(searchName);
    }
  }, [searchName]);

  const handleChange = (value: string) => {
    setLocalName(value);
    onSearchNameChange(value);
    setGroupName(value);
  };

  return (
    <SectionWrapper>
      <Header
        title="Name your search"
        description='How will you recognize this search later? Most users use a mix of location and sector, like "Florida Healthcare" or "Texas Industrial" to stay ahead as they build out more searches.'
      />
      <FlexColumn sx={{ gap: 'padding_8' }}>
        <Text.SmallLabel sx={labelStyle}>Search Name</Text.SmallLabel>
        <Input
          placeholder="e.g. My First Search"
          value={localName}
          onChange={(e) => handleChange(e.target.value)}
          sx={inputStyle}
          autoFocus
        />
      </FlexColumn>
    </SectionWrapper>
  );
};

export default SearchNameStep;
